import React, {useState, useEffect, useRef, useContext} from 'react';
import {X, Send} from 'lucide-react';
import styles from './LiveChatModule.module.css'; // Changed to .module.css
import arrowIcon from '../../static_files/arrow-back-double.svg';

import {initializeApp} from 'firebase/app';
import {getDatabase, ref, push, onValue, serverTimestamp} from 'firebase/database';
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import {shortenAddress} from "../../utils/utils";

const firebaseConfig = {
    apiKey: "AIzaSyDjib-Ji2AE__QF_D1efyf31D2x12xrey4",
    authDomain: "prismaxai.firebaseapp.com",
    databaseURL: "https://prismaxai-default-rtdb.firebaseio.com",
    projectId: "prismaxai",
    storageBucket: "prismaxai.firebasestorage.app",
    messagingSenderId: "514096347185",
    appId: "1:514096347185:web:9e62f2a6a340236937bd10"
};

const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

const LiveChatPanel = ({
                           videoId = 'default-room', // Changed default value
                           maxMessages = 100,
                       }) => {
    const [isChatOpen, setIsChatOpen] = useState(true);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const messagesEndRef = useRef(null);
    const chatRoomRef = useRef(null);
    const unsubscribeRef = useRef(null);
    const {
        showNotification,
        userEmail,
        walletAddress
    } = useContext(SwapMainAppContext);

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        try {
            chatRoomRef.current = ref(database, `chats/${videoId}/messages`);

            // Set up listener
            unsubscribeRef.current = onValue(
                chatRoomRef.current,
                (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        const messageList = Object.entries(data)
                            .map(([key, value]) => ({id: key, ...value, timestamp: typeof value.timestamp === 'number' ? value.timestamp : 0}))
                            .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
                            .slice(-maxMessages);
                        setMessages(messageList);
                    } else {
                        setMessages([]);
                    }
                    setIsConnected(true);
                },
                (error) => {
                    console.error('Firebase connection error:', error);
                    setIsConnected(false);
                }
            );

            // Set connected after setup
            setIsConnected(true);

        } catch (error) {
            console.error('Error setting up Firebase:', error);
            setIsConnected(false);
        }

        // Cleanup
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, [videoId, maxMessages]);


    const sendMessage = async (e) => {
        e.preventDefault();

        const messageText = inputMessage.trim();
        if (!messageText || !isConnected) {
            console.log('Cannot send:', {messageText, isConnected});
            return;
        }
        if (userEmail === '' && walletAddress === '') {
            showNotification('warning', "Please login first")
            return;
        }
        try {
            console.log('Sending message:', messageText);

            // Clear input immediately for better UX
            setInputMessage('');

            // Send to Firebase
            await push(chatRoomRef.current, {
                username: userEmail ? shortenAddress(userEmail) : shortenAddress(walletAddress),
                message: messageText,
                timestamp: serverTimestamp()
            });

            console.log('Message sent successfully');
        } catch (error) {
            console.error('Error sending message:', error);
            // Restore message on error
            setInputMessage(messageText);
        }
    };

    // Format timestamp to time string
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: false
        });
    };

    if (!isChatOpen) {
        return (
            <button
                className={styles.openChatBtn}
                onClick={() => setIsChatOpen(true)}
                style={{
                    position: 'fixed',
                    right: '0px',
                    top: '168px',
                    transformOrigin: 'left center',
                    backgroundColor: '#262626',
                    border: '1px solid #262626',
                    color: '#fff',
                    padding: '18px 20px',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'row',
                    lineHeight: '20px',
                    gap: '16px'
                }}
            >
                <img src={arrowIcon} alt='<<'/>
                Open Live Chat
            </button>
        );
    }

    return (
        <div className={styles.queuePanel}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.title}>LIVE CHAT</h2>
                </div>
                <button
                    className={styles.closeBtn}
                    onClick={() => setIsChatOpen(false)}
                >
                    <X size={20}/>
                </button>
            </div>

            <div className={styles.content}>
                {/* Messages area */}
                <div className={styles.messagesContainer}>
                    {messages.length === 0 ? (
                        <div className={styles.emptyState}>
                            No messages yet. Start the conversation!
                        </div>
                    ) : (
                        messages.map((msg) => (
                            <div key={msg.id} className={styles.messageItem}>
                                <div className={styles.messageHeader}>
                                    <span className={styles.messageUsername}>
                                        {msg.username}
                                    </span>
                                    <span className={styles.messageTime}>
                                        {formatTime(msg.timestamp)}
                                    </span>
                                </div>
                                <div className={styles.messageText}>
                                    {msg.message}
                                </div>
                            </div>
                        ))
                    )}
                    <div ref={messagesEndRef}/>
                </div>
                <div style={{width: '100%', height: '1px', background: '#6B6B6B', marginBottom: '24px'}}/>
                {/* Input area */}
                <form onSubmit={sendMessage} className={styles.inputContainer}>
                    <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder="Send message to the chat..."
                        className={styles.messageInput}
                        disabled={!isConnected}
                        maxLength={200}
                    />
                    <button
                        type="submit"
                        className={styles.sendButton}
                        disabled={!isConnected || !inputMessage.trim()}
                    >
                        <Send size={20}/>
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LiveChatPanel;